import algoliasearch, { SearchClient } from 'algoliasearch/lite';
import { setCookie } from 'nookies';

const ALGOLIA_APP_ID = process.env.NEXT_PUBLIC_ALGOLIA_APP_ID;
const ALGOLIA_API_KEY = process.env.NEXT_PUBLIC_ALGOLIA_API_KEY;

type AlgoliaClientWithInternalProxyParams = {
  loggedId?: string | null;
  sessionId?: string | null;
  channel?: string;
  device?: string | boolean;
  semanticOrchestratorEnabled?: boolean;
  semanticOrchestratorDebugEnabled?: boolean;
};

export default function getAlgoliaClientLite(): SearchClient {
  return algoliasearch(ALGOLIA_APP_ID, ALGOLIA_API_KEY);
}

export function getAlgoliaClientWithInternalProxy({
  loggedId = null,
  sessionId = null,
  channel = 'site',
  device = 'mobile',
  semanticOrchestratorEnabled = false,
  semanticOrchestratorDebugEnabled = false,
}: AlgoliaClientWithInternalProxyParams): SearchClient {
  const baseUrl = process.env.NEXT_PUBLIC_HTTP_DOMAIN_PRINCIPAL
    ? process.env.NEXT_PUBLIC_HTTP_DOMAIN_PRINCIPAL
    : process.env.NEXT_PUBLIC_HTTP_DOMAIN;

  const parsedBaseUrl = baseUrl.replace(/^http(s|):\/\//, '');
  setCookie(
    null,
    'mm-proxy-params',
    JSON.stringify({
      channel,
      device,
      loggedId,
      sessionId,
      semanticOrchestratorEnabled,
      semanticOrchestratorDebugEnabled,
    }),
    {
      path: '/',
      domain: '.madeiramadeira.com.br',
    }
  );

  const protocol = process.env.NODE_ENV === 'development' ? 'http' : 'https';

  const client = algoliasearch(ALGOLIA_APP_ID, ALGOLIA_API_KEY, {
    hosts: [
      { url: `${parsedBaseUrl}/api/search`, protocol },
      { url: `${ALGOLIA_APP_ID}-dsn.algolia.net` },
    ],
    timeouts: {
      connect: 1000,
      read: 5000,
      write: 5000,
    },
  });

  const originalSearch = client.search.bind(client);
  let lastSearchParams: string | null = null;
  let lastSearchResult: any = null;
  let isSearching = false;

  const wrappedClient = {
    ...client,
    search: (requests: any) => {
      const cacheKey = JSON.stringify(
        requests.map((r: any) => ({
          query: r.params?.query,
          indexName: r.indexName,
          page: r.params?.page,
          hitsPerPage: r.params?.hitsPerPage,
          filters: r.params?.filters,
          facetFilters: r.params?.facetFilters,
          numericFilters: r.params?.numericFilters,
        }))
      );

      if (isSearching && cacheKey === lastSearchParams) {
        return new Promise(resolve => {
          const checkInterval = setInterval(() => {
            if (!isSearching && lastSearchResult) {
              clearInterval(checkInterval);
              resolve(lastSearchResult);
            }
          }, 10);
        });
      }

      if (cacheKey === lastSearchParams && lastSearchResult && !isSearching) {
        return Promise.resolve(lastSearchResult);
      }

      isSearching = true;
      lastSearchParams = cacheKey;

      return originalSearch(requests)
        .then((results: any) => {
          isSearching = false;
          lastSearchResult = results;
          return results;
        })
        .catch((error: any) => {
          isSearching = false;
          throw error;
        });
    },
  };

  return wrappedClient as SearchClient;
}
